import {
  POKER_SUIT_COLOR_LOTTIE,
  POKER_SUIT_VIEWPORT,
} from '@/features/LoadingScreen/constants/constants';

type PositionKeyframes = number | { t: number; s: number[] }[];

type ShapeItem = {
  ty?: string;
  c?: { k?: number[] };
  ks?: { k?: { v: [number, number][] } };
  p?: { k: number[] };
};

type LottieLayer = {
  ip?: number;
  op?: number;
  ks: {
    p: {
      x: { k: PositionKeyframes };
      y: { k: PositionKeyframes };
    };
    a: { k: number[] };
    s: { k: number[] };
    r?: { k: PositionKeyframes };
    o?: { k: PositionKeyframes };
  };
  shapes?: { it?: ShapeItem[] }[];
};

const getAxisValueAt = (keyframes: PositionKeyframes, frame: number) => {
  if (!Array.isArray(keyframes)) {
    return keyframes;
  }

  let value = keyframes[0].s?.[0] ?? keyframes[0];
  for (const keyframe of keyframes) {
    if (keyframe.t <= frame) {
      value = keyframe.s?.[0] ?? keyframe;
    } else {
      break;
    }
  }

  return value as number;
};

const getRestPositionAt = (keyframes: PositionKeyframes, holdFrame: number) => {
  if (!Array.isArray(keyframes)) {
    return keyframes;
  }

  const sorted = [...keyframes].sort((a, b) => a.t - b.t);
  const restKeyframe =
    sorted.find((keyframe) => keyframe.t >= holdFrame) ?? sorted.at(-1)!;

  return restKeyframe.s[0];
};

const prependRestPosition = (layer: LottieLayer, holdFrame: number) => {
  const restX = getRestPositionAt(layer.ks.p.x.k, holdFrame);
  const restY = getRestPositionAt(layer.ks.p.y.k, holdFrame);

  const prepend = (
    keyframes: PositionKeyframes,
    rest: number,
  ): PositionKeyframes => {
    if (!Array.isArray(keyframes)) {
      return keyframes;
    }

    if (keyframes.some((keyframe) => keyframe.t === 0)) {
      return keyframes;
    }

    return [{ t: 0, s: [rest] }, ...keyframes];
  };

  layer.ks.p.x.k = prepend(layer.ks.p.x.k, restX);
  layer.ks.p.y.k = prepend(layer.ks.p.y.k, restY);
};

const getVisualBBoxCenter = (layer: LottieLayer, frame: number) => {
  const px = getAxisValueAt(layer.ks.p.x.k, frame);
  const py = getAxisValueAt(layer.ks.p.y.k, frame);
  const ax = layer.ks.a.k[0];
  const ay = layer.ks.a.k[1];
  const scale = layer.ks.s.k[0] / 100;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  layer.shapes?.forEach((shape) => {
    const groupTransform = shape.it?.find((item) => item.ty === 'tr');
    const trx = groupTransform?.p?.k?.[0] ?? 0;
    const try_ = groupTransform?.p?.k?.[1] ?? 0;

    shape.it?.forEach((item) => {
      if (item.ty !== 'sh' || !item.ks?.k?.v) {
        return;
      }

      item.ks.k.v.forEach(([x, y]) => {
        const wx = px + scale * (trx + x - ax);
        const wy = py + scale * (try_ + y - ay);
        minX = Math.min(minX, wx);
        maxX = Math.max(maxX, wx);
        minY = Math.min(minY, wy);
        maxY = Math.max(maxY, wy);
      });
    });
  });

  if (!Number.isFinite(minX)) {
    return { x: px, y: py };
  }

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  };
};

const shiftAxisKeyframes = (keyframes: PositionKeyframes, delta: number) => {
  if (!Array.isArray(keyframes)) {
    return keyframes + delta;
  }

  return keyframes.map((keyframe) => ({
    ...keyframe,
    s: [keyframe.s[0] + delta],
  }));
};

const freezeAnimatedProperty = (
  keyframes: PositionKeyframes,
  frame: number,
) => {
  return getAxisValueAt(keyframes, frame);
};

const freezeLayerAtHoldFrame = (layer: LottieLayer, holdFrame: number) => {
  layer.ks.p.x.k = freezeAnimatedProperty(layer.ks.p.x.k, holdFrame);
  layer.ks.p.y.k = freezeAnimatedProperty(layer.ks.p.y.k, holdFrame);

  if (layer.ks.r?.k) {
    layer.ks.r.k = freezeAnimatedProperty(layer.ks.r.k, holdFrame);
  }

  if (layer.ks.o?.k) {
    layer.ks.o.k = freezeAnimatedProperty(layer.ks.o.k, holdFrame);
  }

  layer.ip = 0;
  layer.op = 1;
};

export const preparePokerSuitAnimation = (data: object, holdFrame: number) => {
  const animation = structuredClone(data) as {
    w: number;
    h: number;
    op?: number;
    layers?: LottieLayer[];
  };

  animation.layers?.forEach((layer) => {
    layer.shapes?.forEach((shape) => {
      shape.it?.forEach((item) => {
        if (item.ty === 'fl' && item.c?.k) {
          item.c.k = [...POKER_SUIT_COLOR_LOTTIE];
        }
      });
    });
  });

  const layer = animation.layers?.[0];
  if (!layer) {
    return animation;
  }

  prependRestPosition(layer, holdFrame);

  const visualCenter = getVisualBBoxCenter(layer, holdFrame);
  const center = POKER_SUIT_VIEWPORT / 2;

  layer.ks.p.x.k = shiftAxisKeyframes(layer.ks.p.x.k, center - visualCenter.x);
  layer.ks.p.y.k = shiftAxisKeyframes(layer.ks.p.y.k, center - visualCenter.y);

  freezeLayerAtHoldFrame(layer, holdFrame);

  animation.w = POKER_SUIT_VIEWPORT;
  animation.h = POKER_SUIT_VIEWPORT;
  animation.op = 1;

  return animation;
};
